import { Component, inject, OnDestroy, OnInit } from '@angular/core';

import { ActivatedRoute, Params, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { Table } from '@cureskin/api-client';
import { RegimenClass } from '@cureskin/api-client/src/server-api';
import { ConnectionService } from '@services/connection-service';
import { ConsultationSessionService } from '@services/consultation-session-service';
import { CurrentComponentService } from '@services/current-component';
import { ExperimentService } from '@services/experiment/experiment.service';
import { AppConfig } from 'client/app/app.config';
import { ChatProgressService } from '@services/chat-progress-service/chat-progress.service';
import { ChatV2ViewModule } from 'client/app/chatV2-view/chatV2-view.module';
import { REGIMEN_QUERY_PARAMS } from '../regimen.constants';

@Component({
  selector: 'consultation-session-chat',
  standalone: true,
  imports: [ChatV2ViewModule],
  templateUrl: './consultation-session-chat.component.html',
  styles: ':host { display: flex; flex-direction: column; flex: 1 1 auto; min-height: 0; }',
})
export class ConsultationSessionChatComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly conn = inject(ConnectionService);
  private readonly consultationSessionService = inject(ConsultationSessionService);
  private readonly currentComponentService = inject(CurrentComponentService);
  private readonly experimentService = inject(ExperimentService);
  private readonly chatProgressService = inject(ChatProgressService);
  public readonly appConfig = inject(AppConfig);

  consultationSession?: Table.ConsultationSession;
  user?: Table.User;
  regimenClass!: RegimenClass;
  isChatLoading: boolean = false;
  private routeSub?: Subscription;

  get activeChatSession(): Table.ConsultationSession[] {
    return this.consultationSession ? [this.consultationSession] : [];
  }

  ngOnInit(): void {
    this.currentComponentService.set(this);
    void this.watchRouteForClass();
  }

  /** Waits for experiments, then re-resolves the session whenever the ?class query param changes. */
  private async watchRouteForClass(): Promise<void> {
    await this.experimentService.experimentsLoaded;
    this.routeSub = this.route.queryParams.subscribe((params: Params): void => {
      const regimenClass = (params[REGIMEN_QUERY_PARAMS.CLASS] as RegimenClass)
        || this.appConfig.Shared.Regimen.Class.FACE;
      if (regimenClass === this.regimenClass) return;
      void this.loadConsultationForClass(regimenClass);
    });
  }

  private async loadConsultationForClass(regimenClass: RegimenClass): Promise<void> {
    this.regimenClass = regimenClass;
    const session: Table.ConsultationSession = await this.conn.findConsultationSession(regimenClass);
    if (!session?.get('PrivateMainConcern')) {
      await this.router.navigate(['/regimen'], { queryParams: { class: regimenClass }, replaceUrl: true });
      return;
    }
    this.consultationSession = session;
    this.user = this.conn.getActingUser();
    try {
      await this.conn.switchUserToState(
        `v4_${session.get('PrivateMainConcernClass').toLowerCase()}:`,
        { consultationSessionId: session.id });
    } catch {
      // switchUserToState failure must not block the chat (matches monolith behaviour)
    }
  }

  handleLoadingStatus(isLoading: boolean): void {
    this.isChatLoading = isLoading;
  }

  onBotReplyInProcessChange(botReplyState: boolean): void {
    // Bridge to the shared service so the shell's header pill can read it across the route-outlet boundary.
    this.chatProgressService.botReplyInProgress.set(botReplyState);
  }

  async handleBackPress(): Promise<boolean> {
    if (this.consultationSession) {
      const sheetRef = this.consultationSessionService.validateAndOpenSkipConsultationQuestionsSheet(
        this.user?.get('PatientName')?.split(' ')[0],
        this.experimentService.chatSkipLastQuestionsExperiment,
        this.consultationSession,
      );
      if (sheetRef) return true;
    }
    return this.router.navigate(['/user'], { queryParams: { tab: 'home' } });
  }

  ngOnDestroy(): void {
    this.routeSub?.unsubscribe();
    this.currentComponentService.remove(this);
  }
}
